/**
 * useVisitorIntent — quietly infers whether the current visitor is a
 * shopper (buyer) or a brand/creator (seller) from their recent route
 * journey. Persists to localStorage under `sb_visitor_intent` so the
 * trial ribbon can pick audience-appropriate copy without a network call.
 *
 * Route → intent mapping:
 *   • buyer  : /shop*, /ai-cart*, /sancta*, /discovery*, /wishlist*, /score
 *   • seller : /pricing, /signup, /for-brands, /admin*, /workspace/*
 *
 * We never overwrite a manual override (see `setManualAudience`) — this
 * hook only sets the passive "inferred" bucket.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import type { RibbonAudience } from "@/components/marketing/ribbonVariants";

const STORAGE_KEY = "sb_visitor_intent";

const BUYER_PATTERNS: RegExp[] = [
  /^\/shop(\/|$)/,
  /^\/ai-cart(\/|$)/,
  /^\/sancta(\/|$)/,
  /^\/discovery(\/|$)/,
  /^\/wishlist(\/|$)/,
  /^\/score(\/|$)/,
  /^\/compare(\/|$)/,
];

const SELLER_PATTERNS: RegExp[] = [
  /^\/pricing(\/|$)/,
  /^\/signup(\/|$)/,
  /^\/for-brands(\/|$)/,
  /^\/admin(\/|$)/,
  /^\/workspace(\/|$)/,
  /^\/dashboard(\/|$)/,
  /^\/products(\/|$)/,
];

function classify(pathname: string): RibbonAudience | null {
  for (const rx of BUYER_PATTERNS) if (rx.test(pathname)) return "buyer";
  for (const rx of SELLER_PATTERNS) if (rx.test(pathname)) return "seller";
  return null;
}

export function useVisitorIntent(): void {
  const { pathname } = useLocation();
  useEffect(() => {
    const next = classify(pathname);
    if (!next) return;
    try {
      const current = localStorage.getItem(STORAGE_KEY);
      if (current !== next) localStorage.setItem(STORAGE_KEY, next);
    } catch { /* private mode / SSR */ }
  }, [pathname]);
}
